import React, { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import siteAnalyticsService, { OverviewResponse } from '../../services/siteAnalytics.service';
import { useDateRange, RangeSelector, LoadError, fa, fmtDuration, faDecimal } from './useDateRange';
import { PALETTE, defaultLineOptions } from './chartConfig';
import './analytics.css';

const SiteAnalyticsOverview: React.FC = () => {
  const dr = useDateRange('7d');
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [activity, setActivity] = useState<{ DAU: number; WAU: number; MAU: number; stickinessPct: number } | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  const fetchAll = async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    try {
      const [overview, act] = await Promise.all([
        siteAnalyticsService.getOverview(dr.range),
        // پرمیشن جدا (admin_analytics_activity) دارد؛ 403 نباید کاربر را ریدایرکت کند —
        // فقط کارت «کاربران فعال» پنهان می‌شود.
        siteAnalyticsService.getActivityStats({ silentForbidden: true }).catch(() => null),
      ]);
      if (reqId !== reqIdRef.current) return; // پاسخ کهنه — نادیده
      setData(overview);
      setActivity(act);
    } catch (e: any) {
      if (reqId !== reqIdRef.current) return;
      setErr(e?.message || 'خطا در دریافت داده');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dr.range.from, dr.range.to, dr.refreshKey]);

  /** higherIsBad: برای متریک‌هایی مثل Bounce که «افزایش» بد است، رنگ‌ها برعکس می‌شود. */
  const changeBadge = (n: number, higherIsBad = false) => {
    if (n > 0) return <span className={`change ${higherIsBad ? 'down' : 'up'}`}>▲ {fa(n)}٪</span>;
    if (n < 0) return <span className={`change ${higherIsBad ? 'up' : 'down'}`}>▼ {fa(Math.abs(n))}٪</span>;
    return <span className="change flat">—</span>;
  };

  // Chart data — تاریخ‌ها کلید روز تهران‌اند؛ T12:00:00 از جابه‌جایی یک‌روزه در مرورگرهای
  // با آفست منفی جلوگیری می‌کند.
  // «بازدید صفحه» یک مرتبه‌ی بزرگی از بقیه بزرگ‌تر است → محور y دوم (سمت چپ)
  // تا خط‌های بازدیدکننده/جدید به کف نمودار نچسبند و روند واقعاً خوانا باشد.
  const lineData = {
    labels: (data?.timeSeries || []).map((p) =>
      new Date(p.date + 'T12:00:00').toLocaleDateString('fa-IR', { month: 'short', day: 'numeric' })
    ),
    datasets: [
      {
        label: 'سشن‌ها',
        data: (data?.timeSeries || []).map((p) => p.sessions),
        borderColor: PALETTE.primary,
        backgroundColor: PALETTE.primaryLight,
        tension: 0.35,
        fill: true,
        yAxisID: 'y',
      },
      {
        label: 'بازدیدکنندگان منحصربه‌فرد',
        data: (data?.timeSeries || []).map((p) => p.visitors),
        borderColor: PALETTE.success,
        backgroundColor: PALETTE.successLight,
        tension: 0.35,
        yAxisID: 'y',
      },
      {
        label: 'بازدید صفحه (محور چپ)',
        data: (data?.timeSeries || []).map((p) => p.pageViews),
        borderColor: PALETTE.warning,
        backgroundColor: PALETTE.warningLight,
        tension: 0.35,
        borderDash: [6, 3],
        yAxisID: 'y1',
      },
      {
        label: 'بازدیدکننده جدید',
        data: (data?.timeSeries || []).map((p) => p.newVisitors),
        borderColor: PALETTE.purple,
        backgroundColor: PALETTE.purpleLight,
        tension: 0.35,
        yAxisID: 'y',
      },
    ],
  };

  const lineOptions = {
    ...defaultLineOptions,
    scales: {
      ...defaultLineOptions.scales,
      y1: { beginAtZero: true, position: 'left' as const, grid: { display: false } },
    },
  };

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2>نمای کلی آمار سایت</h2>
        <div className="analytics-controls">
          <RangeSelector {...dr} />
          <button onClick={dr.refresh} disabled={loading}>
            {loading ? 'در حال بارگذاری…' : 'بروزرسانی'}
          </button>
        </div>
      </div>

      {err && <LoadError message={err} onRetry={fetchAll} />}
      {loading && !data && <div className="analytics-loading">در حال بارگذاری…</div>}

      {/* KPI cards */}
      {data && (
        <div className="analytics-kpi-grid">
          <div className="analytics-kpi-card">
            <div className="accent" />
            <div className="label">بازدیدکنندگان منحصربه‌فرد</div>
            <div className="value">{fa(data.metrics.visitors)}</div>
            {changeBadge(data.change.visitors)}
          </div>
          <div className="analytics-kpi-card green">
            <div className="accent" />
            <div className="label">سشن‌ها</div>
            <div className="value">{fa(data.metrics.sessions)}</div>
            {changeBadge(data.change.sessions)}
          </div>
          <div className="analytics-kpi-card orange">
            <div className="accent" />
            <div className="label">بازدید صفحه</div>
            <div className="value">{fa(data.metrics.pageViews)}</div>
            {changeBadge(data.change.pageViews)}
          </div>
          <div className="analytics-kpi-card purple">
            <div className="accent" />
            <div className="label">بازدیدکننده جدید</div>
            <div className="value">{fa(data.metrics.newVisitors)}</div>
            <span className="change flat">
              {data.metrics.visitors
                ? `${fa(Math.round((data.metrics.newVisitors / data.metrics.visitors) * 100))}٪ از کل`
                : ''}
            </span>
          </div>
          <div className="analytics-kpi-card teal">
            <div className="accent" />
            <div className="label">میانگین مدت سشن</div>
            <div className="value">{fmtDuration(data.metrics.avgSessionDurationSec)}</div>
            {changeBadge(data.change.avgSessionDuration)}
            {typeof data.metrics.avgEngagedDurationSec === 'number' && (
              <span className="change flat" style={{ display: 'block' }}>
                سشن‌های درگیر: {fmtDuration(data.metrics.avgEngagedDurationSec)}
              </span>
            )}
          </div>
          <div className="analytics-kpi-card red">
            <div className="accent" />
            <div className="label">Bounce Rate</div>
            <div className="value">{fa(data.metrics.bounceRate)}٪</div>
            {changeBadge(data.change.bounceRate, true)}
          </div>
          <div className="analytics-kpi-card">
            <div className="accent" />
            <div className="label">صفحات per Session</div>
            <div className="value">{faDecimal(data.metrics.pagesPerSession)}</div>
          </div>
          <div className="analytics-kpi-card green">
            <div className="accent" />
            <div className="label">تبدیل‌های هدف (Goal)</div>
            <div className="value">{fa(data.metrics.conversions)}</div>
            <span className="change flat">خرید، ثبت‌نام، افزودن به سبد…</span>
          </div>
          {/* 🆕 KPIهای تجاری — از سفارش‌های PAID واقعی */}
          <div className="analytics-kpi-card green">
            <div className="accent" />
            <div className="label">خرید (سفارش پرداخت‌شده)</div>
            <div className="value">{fa(data.metrics.purchases || 0)}</div>
            {typeof data.change.purchases === 'number' ? changeBadge(data.change.purchases) : null}
            <span className="change flat" style={{ display: 'block' }}>
              نرخ تبدیل: {faDecimal(data.metrics.purchaseConversionPct || 0)}٪
            </span>
          </div>
          <div className="analytics-kpi-card orange">
            <div className="accent" />
            <div className="label">درآمد (تومان)</div>
            <div className="value">{fa(data.metrics.revenue || 0)}</div>
            {typeof data.change.revenue === 'number' ? changeBadge(data.change.revenue) : null}
            <span className="change flat" style={{ display: 'block' }}>
              میانگین سفارش: {fa(data.metrics.avgOrderValue || 0)}
            </span>
          </div>
        </div>
      )}

      {/* Activity stats */}
      {activity && (
        <div className="analytics-section">
          <div className="analytics-section-header">
            <h3>کاربران فعال</h3>
          </div>
          <div className="analytics-kpi-grid" style={{ marginBottom: 0 }}>
            <div className="analytics-kpi-card">
              <div className="accent" />
              <div className="label">روزانه (DAU)</div>
              <div className="value">{fa(activity.DAU)}</div>
            </div>
            <div className="analytics-kpi-card green">
              <div className="accent" />
              <div className="label">هفتگی (WAU)</div>
              <div className="value">{fa(activity.WAU)}</div>
            </div>
            <div className="analytics-kpi-card purple">
              <div className="accent" />
              <div className="label">ماهانه (MAU)</div>
              <div className="value">{fa(activity.MAU)}</div>
            </div>
            <div className="analytics-kpi-card orange">
              <div className="accent" />
              <div className="label">Stickiness (DAU/MAU)</div>
              <div className="value">{fa(activity.stickinessPct)}٪</div>
            </div>
          </div>
        </div>
      )}

      {/* Time series chart */}
      <div className="analytics-section">
        <div className="analytics-section-header">
          <h3>روند بازدید در بازه‌ی انتخابی</h3>
        </div>
        <div className="analytics-chart-container large">
          {data && data.timeSeries.length > 0 ? (
            <Line data={lineData} options={lineOptions} />
          ) : (
            <div className="analytics-empty">داده‌ای برای نمایش وجود ندارد</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteAnalyticsOverview;
